import { enumLoadStatus } from '@rematter/frontend-lib-api-client';
import { atom, Getter } from 'jotai';
import { atomFamily } from 'jotai/utils';

import { salesOrderCloneAtom } from '../_graphql/mutationSalesOrderClone';
import { salesOrderDeleteAtom } from '../_graphql/mutationSalesOrderDelete';
import { salesOrderUpdateAtom } from '../_graphql/mutationSalesOrderUpdate';
import { loadsListStatusQueryAtom } from '../_graphql/queryLoadListStatus';
import { loadsListQueryAtom } from '../_graphql/queryLoadsList';
import { salesOrderDetailQueryAtom } from '../_graphql/querySalesOrderDetail';
import { salesOrderLineItemsQueryAtom } from '../_graphql/querySalesOrderLineItems';
import { tradeRoutePaths } from '../routes';
import {
  SalesOrderDetailsTab,
  SalesOrderLineItemRecord,
} from '../types/salesOrder';

import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

// 🟣🟣🟣 PARAMS - Sales Order Detail View
export const $salesOrderDetailsPathParams = atomFromRoutePath(
  tradeRoutePaths.salesOrderDetail,
);

export const $salesOrderId = atom((get) =>
  get($salesOrderDetailsPathParams)?.id
    ? String(get($salesOrderDetailsPathParams)?.id)
    : null,
);

// 🟢🟢🟢 REMOTE DATA - Sales Order Detail
export const $remoteSalesOrderDetail = salesOrderDetailQueryAtom((get) => ({
  variables: { id: get($salesOrderId) ?? '' },
  skip: !get($salesOrderId),
}));

// 🟢🟢🟢 REMOTE DATA - Sales Order Detail by ID (for embedded use)
// Uses atomFamily to memoize atoms by ID - prevents infinite re-render loops
//
// Memory management: These atomFamily instances are cleaned up via useAtomFamilyCleanup
// in _SalesOrderDetailView.tsx with a 60-second delay after unmount. This allows quick
// back-navigation without re-fetching while preventing unbounded memory growth.
export const $salesOrderDetailById = atomFamily((salesOrderId: string) =>
  salesOrderDetailQueryAtom(() => ({
    variables: { id: salesOrderId },
    skip: !salesOrderId,
  })),
);

// 🟢🟢🟢 REMOTE DATA - Sales Order Load Status
export const $remoteSalesOrderActiveLoads = atomFamily(
  (salesOrderId?: string | null) =>
    loadsListStatusQueryAtom(() => ({
      variables: {
        status: [
          enumLoadStatus.Pending,
          enumLoadStatus.Received,
          enumLoadStatus.Unassigned,
          enumLoadStatus.Shipped,
        ],
        salesOrderId,
      },
      skip: !salesOrderId,
    })),
);

// 🟢🟢🟢 REMOTE DATA - Sales Order Loads
export const $remoteSalesOrderLoads = loadsListQueryAtom((get) => ({
  variables: {
    salesOrderId: get($salesOrderId) ?? '',
  },
  skip: !get($salesOrderId),
}));

// 🟢🟢🟢 REMOTE DATA - Sales Order Line Items
export const $remoteSalesOrderLineItems = salesOrderLineItemsQueryAtom(
  (get) => ({
    variables: { salesOrderId: get($salesOrderId) ?? '' },
    skip: !get($salesOrderId),
  }),
);

const refetchSalesOrderDetail = (get: Getter) => {
  const salesOrderId = get($salesOrderId);
  if (!salesOrderId) {
    return;
  }
  get($remoteSalesOrderDetail).refetch?.();
};

// 🟠🟠🟠 ACTIONS - Sales Order Update
export const $updateSalesOrder = salesOrderUpdateAtom({
  onSuccess: (get, set) => {
    // Clear draft changes since they've been saved
    set($currentDraftLineItems, null);
    // Refetch sales order detail after successful update
    refetchSalesOrderDetail(get);
    // Update sync state with explicit clear flag
    set($syncDraftWithRemote, true);
  },
});

// 🟠🟠🟠 ACTIONS - Sales Order Delete
export const $deleteSalesOrder = salesOrderDeleteAtom({
  onSuccess: (get) => {
    refetchSalesOrderDetail(get);
  },
});

// 🟠🟠🟠 ACTIONS - Sales Order Clone
export const $cloneSalesOrder = salesOrderCloneAtom({
  onSuccess: (get) => {
    refetchSalesOrderDetail(get);
  },
});

// 🔘🔘🔘 PLAIN ATOMS - UI State
export const $tab = atomFromQueryString.string<SalesOrderDetailsTab>('tab');
export const $isEditMode = atom<boolean>(false);
export const $hasUnsavedChanges = atom<boolean>(false);

export const $isSalesOrderLoading = atom((get) => {
  const data = get($remoteSalesOrderDetail);
  return data?.isLoading ?? false;
});

export const $salesOrderError = atom((get) => {
  const data = get($remoteSalesOrderDetail);
  return data?.error ?? null;
});

// 🟢🟢🟢 COMPUTED DATA - Line Items Data
export const $lineItemsData = atom((get) => {
  const data = get($remoteSalesOrderLineItems);
  return data?.data ?? [];
});

export const $isLineItemsLoading = atom((get) => {
  const data = get($remoteSalesOrderLineItems);
  return data?.isLoading ?? false;
});

// 🟡🟡🟡 DRAFT STATE - Material Line Items Draft Changes
// Store draft line items changes that persist across navigation
export const $draftLineItems = atom<Map<string, SalesOrderLineItemRecord[]>>(
  new Map(),
);

// Computed atom to get draft line items for current sales order
export const $currentDraftLineItems = atom(
  (get) => {
    const salesOrderId = get($salesOrderId);
    const draftMap = get($draftLineItems);
    return salesOrderId ? (draftMap.get(salesOrderId) ?? null) : null;
  },
  (get, set, lineItems: SalesOrderLineItemRecord[] | null) => {
    const salesOrderId = get($salesOrderId);
    if (!salesOrderId) {
      return;
    }

    set($draftLineItems, (prev) => {
      const newMap = new Map(prev);
      if (lineItems === null) {
        newMap.delete(salesOrderId);
      } else {
        newMap.set(salesOrderId, lineItems);
      }
      return newMap;
    });
  },
);

// Computed atom to get effective line items (draft takes precedence over remote)
export const $effectiveLineItems = atom((get) => {
  const salesOrder = get($remoteSalesOrderDetail);
  const draftLineItems = get($currentDraftLineItems);
  const remoteLineItems = salesOrder?.data?.lineItems ?? [];

  // Always prioritize draft changes over remote data to prevent cache issues
  if (draftLineItems !== null) {
    return draftLineItems;
  }

  return remoteLineItems;
});

// Atom to track the last saved line items timestamp to detect external changes
export const $lastSavedLineItemsTimestamp = atom<string | null>(null);

// Computed atom that clears draft state when remote data changes externally
export const $syncDraftWithRemote = atom(
  (get) => {
    const salesOrder = get($remoteSalesOrderDetail);
    const lastTimestamp = get($lastSavedLineItemsTimestamp);

    if (salesOrder?.data?.timestamps?.updatedAt) {
      return salesOrder.data.timestamps.updatedAt.toString() !== lastTimestamp;
    }
    return false;
  },
  (get, set, shouldClearDraft = false) => {
    const salesOrder = get($remoteSalesOrderDetail);
    const currentDraft = get($currentDraftLineItems);

    if (salesOrder?.data?.timestamps?.updatedAt) {
      const currentTimestamp = salesOrder.data.timestamps.updatedAt.toString();

      // Update the timestamp to track the latest data
      set($lastSavedLineItemsTimestamp, currentTimestamp);

      // Only clear draft state if explicitly requested (e.g., after successful save)
      // This prevents cached data from accidentally clearing unsaved changes
      if (shouldClearDraft && currentDraft !== null) {
        set($currentDraftLineItems, null);
      }
    }
  },
);

export const $hasDraftChanges = atom((get) => {
  const salesOrder = get($remoteSalesOrderDetail);
  const draftLineItems = get($currentDraftLineItems);

  if (!salesOrder?.data || draftLineItems === null) {
    return false;
  }

  const originalLineItems = salesOrder.data.lineItems ?? [];

  if (originalLineItems.length !== draftLineItems.length) {
    return true;
  }

  return draftLineItems.some((draftItem, index) => {
    const originalItem = originalLineItems[index];
    if (!originalItem) return true;

    return (
      draftItem.itemId !== originalItem.itemId ||
      draftItem.quantity !== originalItem.quantity ||
      draftItem.unit !== originalItem.unit ||
      draftItem.unitPrice?.amount !== originalItem.unitPrice?.amount ||
      draftItem.unitPrice?.currencyCode !== originalItem.unitPrice?.currencyCode
    );
  });
});

// Action to clear draft changes for current sales order
export const $clearCurrentDraftLineItems = atom(null, (get, set) => {
  set($currentDraftLineItems, null);
});

export const $cleanupDraftState = atom(null, (get, set) => {
  const currentSalesOrderId = get($salesOrderId);
  const draftMap = get($draftLineItems);

  if (!currentSalesOrderId) {
    set($draftLineItems, new Map());
    return;
  }

  const newMap = new Map();
  const currentDraft = draftMap.get(currentSalesOrderId);
  if (currentDraft) {
    newMap.set(currentSalesOrderId, currentDraft);
  }
  set($draftLineItems, newMap);
});

// 🟡🟡🟡 DRAFT STATE BY ID - For embedded use where salesOrderId is passed as prop
export const $currentDraftLineItemsById = atomFamily((salesOrderId: string) =>
  atom(
    (get) => {
      const draftMap = get($draftLineItems);
      return salesOrderId ? (draftMap.get(salesOrderId) ?? null) : null;
    },
    (get, set, lineItems: SalesOrderLineItemRecord[] | null) => {
      if (!salesOrderId) {
        return;
      }

      set($draftLineItems, (prev) => {
        const newMap = new Map(prev);
        if (lineItems === null) {
          newMap.delete(salesOrderId);
        } else {
          newMap.set(salesOrderId, lineItems);
        }
        return newMap;
      });
    },
  ),
);

export const $effectiveLineItemsById = atomFamily((salesOrderId: string) =>
  atom((get) => {
    const salesOrder = get($salesOrderDetailById(salesOrderId));
    const draftLineItems = get($currentDraftLineItemsById(salesOrderId));
    const remoteLineItems = salesOrder?.data?.lineItems ?? [];

    if (draftLineItems !== null) {
      return draftLineItems;
    }

    return remoteLineItems;
  }),
);

export const $hasDraftChangesById = atomFamily((salesOrderId: string) =>
  atom((get) => {
    const salesOrder = get($salesOrderDetailById(salesOrderId));
    const draftLineItems = get($currentDraftLineItemsById(salesOrderId));

    if (!salesOrder?.data || draftLineItems === null) {
      return false;
    }

    const originalLineItems = salesOrder.data.lineItems ?? [];

    if (originalLineItems.length !== draftLineItems.length) {
      return true;
    }

    return draftLineItems.some((draftItem, index) => {
      const originalItem = originalLineItems[index];
      if (!originalItem) return true;

      return (
        draftItem.itemId !== originalItem.itemId ||
        draftItem.quantity !== originalItem.quantity ||
        draftItem.unit !== originalItem.unit ||
        draftItem.unitPrice?.amount !== originalItem.unitPrice?.amount ||
        draftItem.unitPrice?.currencyCode !==
          originalItem.unitPrice?.currencyCode
      );
    });
  }),
);

type OptimisticNotes = {
  internalNotes?: string;
  externalNotes?: string;
};

// 🟡🟡🟡 OPTIMISTIC STATE - Notes (persists across tab switches)
export const $optimisticNotes = atom<Map<string, OptimisticNotes>>(new Map());

export const $optimisticNotesById = atomFamily((salesOrderId: string) =>
  atom(
    (get) => {
      const notesMap = get($optimisticNotes);
      return salesOrderId ? (notesMap.get(salesOrderId) ?? null) : null;
    },
    (get, set, notes: OptimisticNotes | null) => {
      if (!salesOrderId) return;
      set($optimisticNotes, (prev) => {
        const newMap = new Map(prev);
        if (notes === null) {
          newMap.delete(salesOrderId);
        } else {
          const existing = newMap.get(salesOrderId) ?? {};
          newMap.set(salesOrderId, { ...existing, ...notes });
        }
        return newMap;
      });
    },
  ),
);

export const $effectiveNotesById = atomFamily((salesOrderId: string) =>
  atom((get) => {
    const salesOrder = get($salesOrderDetailById(salesOrderId));
    const optimisticNotes = get($optimisticNotesById(salesOrderId));
    const remoteInternalNotes = salesOrder?.data?.recyclerInternalNotes ?? '';
    const remoteExternalNotes = salesOrder?.data?.recyclerExternalNotes ?? '';

    return {
      internalNotes: optimisticNotes?.internalNotes ?? remoteInternalNotes,
      externalNotes: optimisticNotes?.externalNotes ?? remoteExternalNotes,
    };
  }),
);
